//Built-in libraries

//Third party libraries
import { ArrowsRightLeftIcon, EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams, useSearchParams } from 'react-router';
import { addTempLead, removeApplicationTeamMember, removeTempLead } from '@/api/Rover/applicationTeam';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
//Custom components
import Confirm from '@/components/UI/Modal/Confirm';
import SimpleDropDown, { DropDownItem } from '@/components/UI/SimpleDropDown';
//Assets and Miscellaneous
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
//Services
import { notifyUser } from '@/store/Toast';
import { getInitials, sanitizeResponseError } from '@/utils';

interface IApplicationTeamMemberProps {
  userId: number;
  user: string;
  profilePic: string;
  isTempLead: boolean;
  isApplicationLead: boolean;
  handleChangeApplicationLead?: () => void;
}

function RoverTeamMember({
  userId,
  user,
  profilePic,
  isTempLead,
  isApplicationLead,
  handleChangeApplicationLead,
}: IApplicationTeamMemberProps) {
  const { applicationId } = useParams<string>();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const setSearchParams = useSearchParams()[1];
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const removeApplicationMember = useMutation(
    (userId: number) =>
      removeApplicationTeamMember({
        applicationId: applicationId,
        userId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATIONS_TEAM_MEMBERS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${user} removed from the project team successfully.`,
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        cancelRemoveApplicationMemberHandler();
        const resError = sanitizeResponseError(error);

        if (resError.includes('re-assign')) {
          setSearchParams({ user_id: userId.toString() });
          dispatch(
            notifyUser({
              showToast: true,
              toastMessage: `${user} has incomplete assigned items. Please re-assign or change status to complete or delete.`,
              toastType: 'failed',
            })
          );
        } else {
          dispatch(
            notifyUser({
              showToast: true,
              toastMessage: resError,
              toastType: 'failed',
            })
          );
        }
      },
    }
  );

  const removeApplicationMemberHandler = () => {
    if (userId) {
      removeApplicationMember.mutate(userId);
    }
  };

  const cancelRemoveApplicationMemberHandler = () => {
    setShowRemoveConfirm(false);
  };

  const addTempLeadMember = useMutation(
    (userId: number) =>
      addTempLead({
        applicationId: applicationId,
        userId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATIONS_TEAM_MEMBERS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${user} has been set as a temporary lead.`,
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        cancelRemoveApplicationMemberHandler();
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const removeTempLeadMember = useMutation(
    (userId: number) =>
      removeTempLead({
        applicationId: applicationId,
        userId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATIONS_TEAM_MEMBERS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${user} has been removed from temporary lead.`,
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        cancelRemoveApplicationMemberHandler();
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleTeamLead = (isLead: boolean) => {
    if (isLead) {
      removeTempLeadMember.mutate(userId);
    } else {
      addTempLeadMember.mutate(userId);
    }
  };

  const memberProfilePath = `/app/administration/users/${userId}`;

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove Application Member'
          message={`You are about to remove ${user} from the application team. Are you sure ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={removeApplicationMemberHandler}
          decline={cancelRemoveApplicationMemberHandler}
        />
      )}
      <div className='flex justify-between items-center py-3 px-2'>
        <Link to={memberProfilePath}>
          <div className='flex items-center gap-4'>
            <ImageCircularAvatar imageUrl={profilePic} fallbackText={getInitials(user)} />
            <figcaption className='break-words text-left flex flex-col'>
              <span className='text-primaryColor-normal font-bold sm:text-xs 2xl:text-sm'>
                {user} {isTempLead && <Badge text='Temp Lead' badgeType='secondary' />}
                {isApplicationLead && <Badge text='Lead' badgeType='secondary' />}
              </span>
            </figcaption>
          </div>
        </Link>
        {isApplicationLead && (
          <Button
            label='Change Lead'
            btnStyle='btn-neutral'
            btnType='button'
            onClick={handleChangeApplicationLead}
            icon={<ArrowsRightLeftIcon className='h-5 w-5' />}
          />
        )}
        {!isApplicationLead && (
          <div className='text-neutralColor-normal cursor-pointer -mr-2 hover:bg-neutralColor-light rounded-full p-1'>
            <SimpleDropDown toggle={<EllipsisVerticalIcon className='w-5 h-5' />}>
              <DropDownItem id='lead' onClick={() => handleTeamLead(isTempLead)}>
                {isTempLead ? 'Remove' : 'Make'} Temporary Lead
              </DropDownItem>
              <DropDownItem id='archive' onClick={() => setShowRemoveConfirm(true)}>
                Remove
              </DropDownItem>
            </SimpleDropDown>
          </div>
        )}
      </div>
    </>
  );
}
export default RoverTeamMember;
